import React, { useState, useEffect } from 'react';
import {
  Modal,
  Button,
  SpaceBetween,
  Table,
  Box,
  TextFilter,
  Pagination,
  Header,
  Badge
} from '@cloudscape-design/components';
import { titleAPI } from '../services/api';

const ParentSelector = ({ visible, onDismiss, onSelect, contentType }) => {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [filterText, setFilterText] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Determine what content types can be parents
  const getParentTypes = (childType) => {
    switch (childType) {
      case 'season':
        return ['series'];
      case 'episode':
        return ['season'];
      default:
        return ['series', 'season']; // Allow flexibility
    }
  };

  useEffect(() => {
    if (visible) {
      loadPotentialParents();
    }
  }, [visible, contentType]);

  useEffect(() => {
    // Filter items based on search text
    const filtered = items.filter(item => {
      const title = item.metadata?.title || '';
      const type = item.content_type || '';
      return title.toLowerCase().includes(filterText.toLowerCase()) ||
             type.toLowerCase().includes(filterText.toLowerCase());
    });
    setFilteredItems(filtered);
    setCurrentPage(1);
  }, [items, filterText]);

  const loadPotentialParents = async () => {
    setLoading(true);
    try {
      const parentTypes = getParentTypes(contentType);
      const response = await titleAPI.listTitles({ 
        limit: 100,
        content_type: parentTypes.join(',')
      });
      
      // Filter to only show potential parents
      const potentialParents = response.data.items.filter(item => 
        parentTypes.includes(item.content_type)
      );
      
      setItems(potentialParents);
    } catch (error) {
      console.error('Failed to load potential parents:', error);
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = () => {
    if (selectedItems.length > 0) {
      onSelect(selectedItems[0]);
      onDismiss();
    }
  };

  // Pagination
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedItems = filteredItems.slice(startIndex, endIndex);

  const columnDefinitions = [
    {
      id: 'title',
      header: 'Title',
      cell: item => item.metadata?.title || 'Untitled',
      sortingField: 'metadata.title'
    },
    {
      id: 'type',
      header: 'Type',
      cell: item => <Badge>{item.content_type}</Badge>,
      width: 100
    },
    {
      id: 'year',
      header: 'Year',
      cell: item => item.metadata?.year || '-',
      width: 80
    },
    {
      id: 'id',
      header: 'ID',
      cell: item => (
        <Box fontSize="body-s" color="text-body-secondary">
          {item.title_id.substring(0, 8)}...
        </Box>
      ),
      width: 120
    }
  ];

  return (
    <Modal
      visible={visible}
      onDismiss={onDismiss}
      header="Select Parent Content"
      size="large"
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={onDismiss}>
              Cancel
            </Button>
            <Button 
              variant="primary" 
              onClick={handleSelect}
              disabled={selectedItems.length === 0}
            >
              Select Parent
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="m">
        <Box>
          Select a parent {getParentTypes(contentType).join(' or ')} for this {contentType}.
        </Box>
        
        <Table
          columnDefinitions={columnDefinitions}
          items={paginatedItems}
          loading={loading}
          loadingText="Loading potential parents..."
          selectionType="single"
          selectedItems={selectedItems}
          onSelectionChange={({ detail }) => setSelectedItems(detail.selectedItems)}
          empty={
            <Box textAlign="center" color="inherit">
              <b>No potential parents found</b>
              <Box variant="p" color="inherit">
                Create a {getParentTypes(contentType)[0]} first.
              </Box>
            </Box>
          }
          filter={
            <TextFilter
              filteringText={filterText}
              onChange={({ detail }) => setFilterText(detail.filteringText)}
              filteringPlaceholder="Search parents..."
            />
          }
          header={
            <Header
              counter={`(${filteredItems.length})`}
            >
              Available Parents
            </Header>
          }
          pagination={
            filteredItems.length > itemsPerPage && (
              <Pagination
                currentPageIndex={currentPage}
                pagesCount={Math.ceil(filteredItems.length / itemsPerPage)}
                onChange={({ detail }) => setCurrentPage(detail.currentPageIndex)}
              />
            )
          }
        />
      </SpaceBetween>
    </Modal>
  );
};

export default ParentSelector;